/**
 * Leads — the staff lead queue.
 *
 * Lists SUBMITTED projects across all customers, already sorted server-side by
 * star rating then timeline urgency. Staff-only (role-gated + backend 403s
 * non-staff). Shows the internal engine total and lead score — never customer-
 * facing. Clicking a lead opens the estimate editor.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeads, getMe, type Lead } from '../../lib/api';

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function stars(score: number | null): string {
  if (score == null) return 'Unscored';
  return '★'.repeat(score) + '☆'.repeat(Math.max(0, 5 - score));
}

export function Leads(): JSX.Element {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMe()
      .then((me) => {
        const ok = me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR';
        setAllowed(ok);
        if (ok) return getLeads().then(setLeads);
        return undefined;
      })
      .catch(() => {
        setAllowed(false);
        setError('We couldn\u2019t load the lead queue.');
      });
  }, []);

  if (allowed === null) {
    return (
      <div className="flow flow--single">
        <div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div>
      </div>
    );
  }
  if (!allowed) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>Not available</h1>
          <p className="stage-card__lede">This area is for staff only.</p>
          <div className="q-actions">
            <button type="button" className="pb-btn" onClick={() => navigate('/dashboard')}>Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Lead queue</h1>
        <p className="stage-card__lede">
          Submitted projects, sorted by fit then urgency. Numbers here are internal &mdash; the
          customer hasn&rsquo;t seen a price.
        </p>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        {!leads ? (
          <p className="stage-card__lede">Loading&hellip;</p>
        ) : leads.length === 0 ? (
          <p className="stage-card__lede">No submitted leads yet.</p>
        ) : (
          <div>
            {leads.map((l) => (
              <button
                key={l.projectId}
                type="button"
                className="pb-option"
                style={{ width: '100%', textAlign: 'left' }}
                onClick={() => navigate(`/admin/estimate/${l.projectId}`)}
              >
                <span style={{ flexGrow: 1 }}>
                  <span className="pb-option__label">
                    {l.projectName ?? 'Project'} &middot; {l.customerName ?? 'Customer'}
                  </span>
                  <span className="pb-option__sub">
                    {[l.addressLine1, l.city, l.state].filter(Boolean).join(', ')}
                    {l.budgetLabel ? ` \u00b7 Budget: ${l.budgetLabel}` : ''}
                    {l.timelineLabel ? ` \u00b7 ${l.timelineLabel}` : ''}
                  </span>
                </span>
                <span style={{ textAlign: 'right', minWidth: '5.5rem' }}>
                  <span className="pb-option__label" style={{ color: 'var(--pb-chalk)' }}>{stars(l.leadScore)}</span>
                  <span className="pb-option__sub">est. {dollars(l.engineTotalCents)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
